import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { RxIntuitiveToothEditorBridgeFacade } from '../../rx-intuitive-tooth-editor-bridge.facade';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { RxIntuitiveBridgeEditorComponent } from '@modules/rx-intuitive-teeth-orchestrator/components/rx-intuitive-bridge-editor/rx-intuitive-bridge-editor.component';
import { BeforeRxSaveService } from '@shared/services/before-rx-save.service';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { fromEvent } from 'rxjs';

@Component({
	selector: 'rx-intuitive-tooth-editor-bridge-container',
	templateUrl: './rx-intuitive-tooth-editor-bridge-container.component.html',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, TranslateModule, RxIntuitiveBridgeEditorComponent]
})
export class RxIntuitiveToothEditorBridgeContainerComponent extends BaseDestroyableComponent implements OnInit {
	readonly bridgeEditorTeeth$ = this.toothEditorFacade.bridgeEditorTeeth$;
	readonly canEditBridgeEditor$ = this.toothEditorFacade.canEditBridgeToothEditor$;
	readonly ponticDesigns$ = this.toothEditorFacade.ponticDesigns$;
	readonly bridgeListItem$ = this.toothEditorFacade.bridgeListItem$;
	readonly bridgeTeethFlat$ = this.toothEditorFacade.selectedBridgeTeeth$;
	readonly toothEditorReadOnly$ = this.toothEditorFacade.toothEditorReadOnly$;
	readonly hasUnsavedChanges$ = this.toothEditorFacade.hasUnsavedChanges$;

	constructor(
		private readonly beforeRxSaveService: BeforeRxSaveService,
		private readonly toothEditorFacade: RxIntuitiveToothEditorBridgeFacade
	) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToSaveRx();

		fromEvent<PointerEvent>(document, 'pointerup')
			.pipe(
				takeUntil(this.componentAlive$),
				withLatestFrom(this.toothEditorFacade.toothEditorReadOnly$),
				filter(([event, isReadOnly]) => !!isReadOnly && !this.isInsideBridgeEditingArea(event.target))
			)
			.subscribe(() => {
				this.toothEditorFacade.closeBridge();
			});
	}

	onSaveBridge(): void {
		this.toothEditorFacade.saveBridge();
	}

	onDeleteBridge(): void {
		this.toothEditorFacade.deleteBridge();
	}

	onRevertChanges(): void {
		this.toothEditorFacade.revertChanges();
	}

	onCloseBridge(): void {
		this.toothEditorFacade.closeBridge();
	}

	onBridgeCrownFormChanged(crownForm: CrownForm): void {
		this.toothEditorFacade.updateBridgeTeethCrownData(crownForm);
	}

	onBridgeAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.toothEditorFacade.updateBridgeTeethByIds(event.toothIds, event.result.values);
	}

	onBridgeClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.toothEditorFacade.updateBridgeTeethByIds(event.toothIds, event.payload);
	}

	onPonticDesignChanged(event: { toothIds: number[]; ponticDesignId: PonticDesignEnum | null }): void {
		this.toothEditorFacade.updateBridgeTeethPonticDesign(event.toothIds, event.ponticDesignId);
	}

	private subscribeToSaveRx(): void {
		this.beforeRxSaveService.beforeSave$
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(() => this.toothEditorFacade.forceSaveBridge());
	}

	private isInsideBridgeEditingArea(target: EventTarget | null): boolean {
		const bridgeAreaSelectors = [
			'rx-intuitive-teeth-diagram',
			'rx-intuitive-bridge-editor',
			'rx-intuitive-bridge-indication-buttons',
			'rx-intuitive-indication-button',
			'rx-intuitive-bridge-diagram-footer',
			'rx-intuitive-bridge-banner',
			'.cdk-overlay-container'
		];

		if (target == null) {
			return false;
		}

		let element: Element;

		if (target instanceof Element) {
			element = target;
		} else if (target instanceof Node && target.parentElement != null) {
			element = target.parentElement;
		} else {
			return false;
		}

		return bridgeAreaSelectors.some(s => element.closest(s) != null);
	}
}
